import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Building2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  GripVertical,
  Home,
  Layers,
  Link2,
  MessageSquare,
  Pencil,
  PhoneCall,
  Plus,
  Settings,
  Star,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { useToast } from '../../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../../components/common/PageState.jsx';
import {
  adminGetOrg,
  adminUpdateOrg,
  adminGrantCredits,
  adminCreateUser,
  adminDeleteUser,
  adminListIntegrations,
  adminSaveGhlIntegration,
  adminSaveTwilioIntegration,
  adminSaveVapiIntegration,
  adminDisconnectIntegration,
  adminTestIntegration,
  adminGetAIConfig,
  adminSaveAIConfig,
  adminListListings,
  adminCreateListing,
  adminUpdateListing,
  adminDeleteListing,
  adminListPipelines,
  adminCreatePipeline,
  adminUpdatePipeline,
  adminDeletePipeline,
  createBillingCheckout,
  getBillingStatus,
} from '../../services/api.js';

const PLAN_SETUP_FEE_USD = {
  starter: 1500,
  growth: 3000,
  enterprise: 5000,
};

const BILLING_STATUS_BADGE = {
  active: 'bg-[#22c55e]/15 text-[#22c55e]',
  unpaid: 'bg-[#f59e0b]/15 text-[#f59e0b]',
  paused: 'bg-[#6b7280]/15 text-[#6b7280]',
  cancelled: 'bg-[#ef4444]/15 text-[#ef4444]',
};

function formatUsd(n) {
  if (n === null || n === undefined) return '—';
  return `$${Number(n).toLocaleString('en-US')}`;
}

const TABS = [
  { key: 'overview', label: 'Overview', icon: Building2 },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'billing', label: 'Billing', icon: CreditCard },
  { key: 'config', label: 'Config', icon: Settings },
];

const ROLE_LABELS = {
  team_leader: 'Team Leader',
  agent: 'Agent',
  isa: 'ISA',
  super_admin: 'Super Admin',
};

const PLAN_TIERS = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
];

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ClientDetail() {
  const { id } = useParams();
  const { showToast } = useToast();

  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetOrg(id);
      setData(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to load client.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const org = data?.org;
  const users = data?.users || [];
  const transactions = data?.transactions || [];
  const crm = data?.crm || null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/clients"
          className="inline-flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={12} />
          Back to Clients
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="page-title">{org?.name || (loading ? 'Loading…' : 'Client')}</h1>
            <p className="page-subtitle">
              {org?.plan_tier ? `${org.plan_tier} plan` : ''}
              {org?.plan_tier && org?.status ? ' · ' : ''}
              {org?.status ? org.status : ''}
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="card p-4">
          <InlineSpinner label="Loading client…" />
        </div>
      )}
      {error && !loading && (
        <ErrorBanner message={error} detail={`admin/orgs/${id}`} onRetry={reload} />
      )}

      {!loading && !error && org && (
        <>
          <div className="flex items-center gap-1 border-b border-border-card">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? 'border-accent text-text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === 'overview' && (
            <OverviewTab org={org} onSaved={reload} showToast={showToast} />
          )}
          {tab === 'users' && (
            <UsersTab orgId={org.id} users={users} onChange={reload} showToast={showToast} />
          )}
          {tab === 'billing' && (
            <BillingTab
              orgId={org.id}
              planTier={org.plan_tier}
              credits={org.credits}
              transactions={transactions}
              onChange={reload}
              showToast={showToast}
            />
          )}
          {tab === 'config' && (
            <ConfigTab orgId={org.id} showToast={showToast} />
          )}
        </>
      )}
    </div>
  );
}

// ── Overview ───────────────────────────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <div className="card p-5">
      <div className="text-xs uppercase tracking-wider text-text-secondary">{label}</div>
      <div className="mt-1.5 text-2xl font-semibold text-text-primary tabular-nums">
        {value}
      </div>
    </div>
  );
}

function OverviewTab({ org, onSaved, showToast }) {
  const [form, setForm] = useState({
    name: org.name,
    plan_tier: org.plan_tier || 'growth',
    status: org.status || 'active',
    enrichment_credits_monthly: org.credits?.included ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await adminUpdateOrg(org.id, {
        name: form.name,
        plan_tier: form.plan_tier,
        status: form.status,
        enrichment_credits_monthly: Number(form.enrichment_credits_monthly) || 0,
      });
      showToast('Client updated', { type: 'success' });
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Update failed',
        { type: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Users" value={formatNumber(org.users_count)} />
        <StatCard label="Leads" value={formatNumber(org.leads_count)} />
        <StatCard
          label="Showings"
          value={formatNumber(org.showings_count ?? 0)}
        />
        <StatCard
          label="Credits Remaining"
          value={formatNumber(org.credits?.remaining ?? 0)}
        />
      </div>

      <form onSubmit={onSave} className="card p-6 space-y-4">
        <div>
          <h2 className="section-header mb-1">Organization Settings</h2>
          <p className="text-sm text-text-secondary">
            Core details about this client. Updates here are visible to their
            team immediately.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
              Name
            </label>
            <input className="input" value={form.name} onChange={update('name')} required />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
              Plan Tier
            </label>
            <select className="input" value={form.plan_tier} onChange={update('plan_tier')}>
              {PLAN_TIERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
              Status
            </label>
            <select className="input" value={form.status} onChange={update('status')}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
              Monthly Credit Quota
            </label>
            <input
              className="input"
              type="number"
              min="0"
              value={form.enrichment_credits_monthly}
              onChange={update('enrichment_credits_monthly')}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────────

function UsersTab({ orgId, users, onChange, showToast }) {
  const [showAdd, setShowAdd] = useState(false);

  const onDelete = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This can be reversed by support.`)) return;
    try {
      await adminDeleteUser(u.id);
      showToast(`Removed ${u.name}`, { type: 'success' });
      onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Delete failed',
        { type: 'error' },
      );
    }
  };

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border-card">
        <div>
          <h2 className="section-header mb-0">Team Members</h2>
          <p className="text-xs text-text-secondary mt-1">
            {users.length} {users.length === 1 ? 'user' : 'users'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <UserPlus size={14} />
          Add User
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border-card">
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Role</th>
              <th className="px-5 py-3 font-semibold">Last Login</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-8 text-center text-text-secondary"
                >
                  No users yet.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-b border-border-card/60 hover:bg-bg-subtle transition-colors"
              >
                <td className="px-5 py-3 font-medium text-text-primary">{u.name}</td>
                <td className="px-5 py-3 text-text-secondary">{u.email}</td>
                <td className="px-5 py-3 text-text-secondary">
                  {ROLE_LABELS[u.role] || u.role || '—'}
                </td>
                <td className="px-5 py-3 text-text-secondary">
                  {formatDate(u.updated_at)}
                </td>
                <td className="px-5 py-3">
                  <span
                    className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium ${
                      u.is_active
                        ? 'bg-[#22c55e]/15 text-[#16a34a]'
                        : 'bg-[#6b7280]/15 text-[#6b7280]'
                    }`}
                  >
                    {u.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(u)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#dc2626] hover:underline"
                  >
                    <Trash2 size={12} />
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddUserModal
          orgId={orgId}
          onClose={() => setShowAdd(false)}
          onCreated={(u) => {
            showToast(`Added ${u.name}`, { type: 'success' });
            setShowAdd(false);
            onChange();
          }}
        />
      )}
    </section>
  );
}

function AddUserModal({ orgId, onClose, onCreated }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agent',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminCreateUser(orgId, { ...form, email: form.email.trim() });
      onCreated(res.user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : err.message || 'Create failed';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h3 className="section-header mb-0">Add User</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Name</label>
            <input className="input" value={form.name} onChange={update('name')} required />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Email</label>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={update('email')}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">
              Temporary Password
            </label>
            <input
              className="input"
              type="password"
              value={form.password}
              onChange={update('password')}
              minLength={8}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Role</label>
            <select className="input" value={form.role} onChange={update('role')}>
              <option value="team_leader">Team Leader</option>
              <option value="agent">Agent</option>
              <option value="isa">ISA</option>
            </select>
          </div>
          {error && (
            <div className="text-sm text-[#dc2626] bg-[#fef2f2] border border-[#fca5a5] rounded-btn px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn-outlined"
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Billing tab ────────────────────────────────────────────────────────────

function CreditStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-text-secondary">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${accent || 'text-text-primary'}`}
      >
        {formatNumber(value)}
      </div>
    </div>
  );
}

function StripeBillingSection({ orgId, planTier, showToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBillingStatus(orgId);
      setStatus(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to load billing.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const billingStatus = status?.billing_status || 'unpaid';
  const badgeClass =
    BILLING_STATUS_BADGE[billingStatus] || BILLING_STATUS_BADGE.unpaid;
  const displayPlan = status?.plan_tier || planTier || null;
  const setupFeeUsd =
    (displayPlan && PLAN_SETUP_FEE_USD[displayPlan.toLowerCase()]) || null;

  const onGenerate = async () => {
    if (generating) return;
    if (!displayPlan) {
      showToast('Set a plan tier on the Overview tab before billing.', {
        type: 'error',
      });
      return;
    }
    setGenerating(true);
    try {
      const res = await createBillingCheckout({
        orgId,
        planTier: displayPlan.toLowerCase(),
      });
      if (res?.checkout_url) {
        window.open(res.checkout_url, '_blank', 'noopener,noreferrer');
        showToast('Payment link generated', { type: 'success' });
      } else {
        showToast('No checkout URL returned', { type: 'error' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Checkout failed',
        { type: 'error' },
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="card p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="section-header mb-1">Stripe Billing</h2>
          <p className="text-sm text-text-secondary">
            Setup fee charged once via Stripe Checkout. Webhook events flip the
            status below as payments land.
          </p>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          className="btn-primary inline-flex items-center gap-2 shrink-0"
          disabled={generating || loading || !displayPlan}
        >
          <CreditCard size={14} />
          {generating ? 'Generating…' : 'Generate Payment Link'}
        </button>
      </div>

      {loading ? (
        <div className="mt-5">
          <InlineSpinner />
        </div>
      ) : error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary">
              Status
            </div>
            <div className="mt-1.5">
              <span
                className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium capitalize ${badgeClass}`}
              >
                {billingStatus}
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary">
              Plan Tier
            </div>
            <div className="mt-1.5 text-sm text-text-primary capitalize">
              {displayPlan || '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary">
              Setup Fee
            </div>
            <div className="mt-1.5 text-sm text-text-primary tabular-nums">
              {formatUsd(setupFeeUsd)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function BillingTab({ orgId, planTier, credits, transactions, onChange, showToast }) {
  const [showGrant, setShowGrant] = useState(false);

  const balance = credits || { included: 0, used: 0, purchased: 0, remaining: 0, total: 0 };

  return (
    <div className="space-y-6">
      <StripeBillingSection
        orgId={orgId}
        planTier={planTier}
        showToast={showToast}
      />

      <section className="card p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="section-header mb-1">Credit Balance</h2>
            <p className="text-sm text-text-secondary">
              Enrichment credits — included (plan), purchased (top-ups), used
              (debits).
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowGrant(true)}
            className="btn-primary inline-flex items-center gap-2 shrink-0"
          >
            <Plus size={14} />
            Grant Credits
          </button>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-6">
          <CreditStat label="Included" value={balance.included} />
          <CreditStat label="Purchased" value={balance.purchased} />
          <CreditStat label="Used" value={balance.used} />
          <CreditStat
            label="Remaining"
            value={balance.remaining}
            accent="text-accent"
          />
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-border-card">
          <h2 className="section-header mb-0">Transaction History</h2>
          <p className="text-xs text-text-secondary mt-1">
            Every credit grant, top-up, and enrichment debit for this org.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border-card">
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold text-right">Credits</th>
                <th className="px-5 py-3 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-5 py-8 text-center text-text-secondary"
                  >
                    No transactions yet.
                  </td>
                </tr>
              )}
              {transactions.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-border-card/60"
                >
                  <td className="px-5 py-3 text-text-secondary">
                    {formatDate(t.created_at)}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium capitalize ${
                        t.type === 'used'
                          ? 'bg-[#6b7280]/15 text-[#6b7280]'
                          : t.type === 'purchased'
                            ? 'bg-accent/15 text-accent'
                            : 'bg-[#3b82f6]/15 text-[#2563eb]'
                      }`}
                    >
                      {t.type}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {t.type === 'used' ? '−' : '+'}
                    {formatNumber(t.credits)}
                  </td>
                  <td className="px-5 py-3 text-text-secondary">
                    {t.description || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showGrant && (
        <GrantCreditsModal
          orgId={orgId}
          onClose={() => setShowGrant(false)}
          onGranted={() => {
            showToast('Credits granted', { type: 'success' });
            setShowGrant(false);
            onChange();
          }}
        />
      )}
    </div>
  );
}

function GrantCreditsModal({ orgId, onClose, onGranted }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({ credits: 100, type: 'purchased', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const amount = Number(form.credits);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a positive number of credits.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminGrantCredits(orgId, {
        credits: amount,
        type: form.type,
        description: form.description || null,
      });
      onGranted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : err.message || 'Grant failed';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h3 className="section-header mb-0">Grant Credits</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Amount</label>
              <input
                className="input"
                type="number"
                min="1"
                value={form.credits}
                onChange={update('credits')}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Type</label>
              <select className="input" value={form.type} onChange={update('type')}>
                <option value="purchased">Purchased</option>
                <option value="included">Included</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Description</label>
            <input
              className="input"
              value={form.description}
              onChange={update('description')}
              placeholder="e.g. Goodwill top-up for Q2"
            />
          </div>
          {error && (
            <div className="text-sm text-[#dc2626] bg-[#fef2f2] border border-[#fca5a5] rounded-btn px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn-outlined"
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Granting…' : 'Grant Credits'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Config tab ─────────────────────────────────────────────────────────────

const INTEGRATION_BLUEPRINTS = {
  ghl: {
    icon: Link2,
    title: 'Go High Level',
    description:
      'GHL CRM sync. Pulls contacts into Atlacis and pushes enrichment back as custom fields.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'eyJhbGci…',
      },
      {
        key: 'location_id',
        label: 'Location ID',
        type: 'text',
        required: true,
        placeholder: 'ABCDefGhIj…',
      },
    ],
    summaryFields: [
      { key: 'location_id', label: 'Location ID', source: 'config' },
      { key: 'api_key', label: 'API Key', source: 'masked' },
    ],
  },
  twilio: {
    icon: MessageSquare,
    title: 'Twilio',
    description:
      'SMS delivery pipe for hot-lead alerts and lead replies. Requires an Account SID, Auth Token, and sender number.',
    fields: [
      {
        key: 'account_sid',
        label: 'Account SID',
        type: 'password',
        required: true,
        placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      },
      {
        key: 'auth_token',
        label: 'Auth Token',
        type: 'password',
        required: true,
      },
      {
        key: 'phone_number',
        label: 'Sender Phone Number',
        type: 'text',
        required: true,
        placeholder: '+13055550123',
      },
    ],
    summaryFields: [
      { key: 'phone_number', label: 'Sender Number', source: 'config' },
      { key: 'account_sid', label: 'Account SID', source: 'masked' },
      { key: 'auth_token', label: 'Auth Token', source: 'masked' },
    ],
  },
  vapi: {
    icon: PhoneCall,
    title: 'Vapi',
    description:
      'Voice delivery pipe for Victoria and Conor agents. Needs the Vapi API key and the assistant IDs for each agent.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        required: true,
      },
      {
        key: 'victoria_assistant_id',
        label: 'Victoria Assistant ID',
        type: 'text',
        required: false,
      },
      {
        key: 'conor_assistant_id',
        label: 'Conor Assistant ID',
        type: 'text',
        required: false,
      },
    ],
    summaryFields: [
      {
        key: 'victoria_assistant_id',
        label: 'Victoria Assistant ID',
        source: 'config',
      },
      {
        key: 'conor_assistant_id',
        label: 'Conor Assistant ID',
        source: 'config',
      },
      { key: 'api_key', label: 'API Key', source: 'masked' },
    ],
  },
};

const INTEGRATION_ORDER = ['ghl', 'twilio', 'vapi'];

function StatusBadge({ status }) {
  const [label, classes] =
    status === 'connected'
      ? ['Connected', 'bg-[#22c55e]/15 text-[#16a34a]']
      : status === 'error'
        ? ['Error', 'bg-[#ef4444]/15 text-[#dc2626]']
        : ['Not Connected', 'bg-[#6b7280]/15 text-[#6b7280]'];
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      {label}
    </span>
  );
}

function ConfigTab({ orgId, showToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byType, setByType] = useState({});
  const [editing, setEditing] = useState(null); // integration_type currently being edited
  const [busyType, setBusyType] = useState(null); // integration_type with a test/disconnect in flight

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminListIntegrations(orgId);
      const map = {};
      for (const item of res.integrations || []) {
        map[item.integration_type] = item;
      }
      setByType(map);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : err.message || 'Failed to load integrations.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const onSaved = (integrationType, payload) => {
    if (payload?.integration) {
      setByType((prev) => ({
        ...prev,
        [integrationType]: payload.integration,
      }));
    }
    if (payload?.tested_ok) {
      showToast(
        `${INTEGRATION_BLUEPRINTS[integrationType].title} connected`,
        { type: 'success' },
      );
    } else {
      const err = payload?.integration?.last_error || 'unknown error';
      showToast(
        `${INTEGRATION_BLUEPRINTS[integrationType].title} saved but test failed: ${err}`,
        { type: 'error' },
      );
    }
    setEditing(null);
  };

  const onTest = async (integrationType) => {
    if (busyType) return;
    setBusyType(integrationType);
    try {
      const res = await adminTestIntegration(orgId, integrationType);
      setByType((prev) => ({
        ...prev,
        [integrationType]: res.integration,
      }));
      if (res.tested_ok) {
        showToast(
          `${INTEGRATION_BLUEPRINTS[integrationType].title} connection OK`,
          { type: 'success' },
        );
      } else {
        const err = res.integration?.last_error || 'test failed';
        showToast(
          `${INTEGRATION_BLUEPRINTS[integrationType].title} test failed: ${err}`,
          { type: 'error' },
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Test failed',
        { type: 'error' },
      );
    } finally {
      setBusyType(null);
    }
  };

  const onDisconnect = async (integrationType) => {
    const blueprint = INTEGRATION_BLUEPRINTS[integrationType];
    if (
      !window.confirm(
        `Disconnect ${blueprint.title}? The stored credentials will be deleted.`,
      )
    ) {
      return;
    }
    if (busyType) return;
    setBusyType(integrationType);
    try {
      const res = await adminDisconnectIntegration(orgId, integrationType);
      setByType((prev) => ({
        ...prev,
        [integrationType]: res.integration,
      }));
      showToast(`${blueprint.title} disconnected`, { type: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Disconnect failed',
        { type: 'error' },
      );
    } finally {
      setBusyType(null);
    }
  };

  if (loading) {
    return (
      <div className="card p-4">
        <InlineSpinner label="Loading integrations…" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorBanner
        message={error}
        detail={`admin/orgs/${orgId}/integrations`}
        onRetry={load}
      />
    );
  }

  return (
    <div className="space-y-4">
      {INTEGRATION_ORDER.map((type) => (
        <IntegrationCard
          key={type}
          integrationType={type}
          blueprint={INTEGRATION_BLUEPRINTS[type]}
          integration={byType[type] || null}
          busy={busyType === type}
          onConnect={() => setEditing(type)}
          onTest={() => onTest(type)}
          onDisconnect={() => onDisconnect(type)}
        />
      ))}

      <AIConfigSection orgId={orgId} showToast={showToast} />
      <ListingsSection orgId={orgId} showToast={showToast} />
      <PipelinesSection orgId={orgId} showToast={showToast} />
      <WebchatWidgetSection orgId={orgId} showToast={showToast} />

      {editing && (
        <CredentialModal
          orgId={orgId}
          integrationType={editing}
          blueprint={INTEGRATION_BLUEPRINTS[editing]}
          onClose={() => setEditing(null)}
          onSaved={(payload) => onSaved(editing, payload)}
        />
      )}
    </div>
  );
}

function IntegrationCard({
  integrationType,
  blueprint,
  integration,
  busy,
  onConnect,
  onTest,
  onDisconnect,
}) {
  const Icon = blueprint.icon;
  const status = integration?.status || 'disconnected';
  const isConnected = status === 'connected' || status === 'error';

  return (
    <section className="card p-6">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-btn bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <h2 className="section-header mb-1">{blueprint.title}</h2>
          <p className="text-sm text-text-secondary">{blueprint.description}</p>
        </div>
        <StatusBadge status={status} />
      </div>

      {isConnected && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          {blueprint.summaryFields.map((f) => {
            let value = '—';
            if (f.source === 'config') {
              value = integration?.config?.[f.key] || '—';
            } else if (f.source === 'masked') {
              value = integration?.masked?.[f.key] || '—';
            }
            return (
              <div key={f.key}>
                <div className="text-xs uppercase tracking-wider text-text-secondary">
                  {f.label}
                </div>
                <div className="mt-1 text-text-primary font-medium break-all">
                  {value}
                </div>
              </div>
            );
          })}
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary">
              Last Tested
            </div>
            <div className="mt-1 text-text-primary font-medium">
              {formatDate(integration?.last_tested_at)}
            </div>
          </div>
        </div>
      )}

      {status === 'error' && integration?.last_error && (
        <div className="mt-4 text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
          Last error: <span className="font-mono text-xs">{integration.last_error}</span>
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        {!isConnected && (
          <button
            type="button"
            onClick={onConnect}
            className="btn-primary inline-flex items-center gap-2"
            disabled={busy}
          >
            <Plus size={14} />
            Connect
          </button>
        )}
        {isConnected && (
          <>
            <button
              type="button"
              onClick={onTest}
              className="btn-primary inline-flex items-center gap-2"
              disabled={busy}
            >
              {busy ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              type="button"
              onClick={onConnect}
              className="btn-outlined"
              disabled={busy}
            >
              Update Credentials
            </button>
            <button
              type="button"
              onClick={onDisconnect}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#dc2626] hover:underline ml-auto"
              disabled={busy}
            >
              <Trash2 size={14} />
              Disconnect
            </button>
          </>
        )}
      </div>
    </section>
  );
}

const SAVE_FN_BY_TYPE = {
  ghl: adminSaveGhlIntegration,
  twilio: adminSaveTwilioIntegration,
  vapi: adminSaveVapiIntegration,
};

function CredentialModal({ orgId, integrationType, blueprint, onClose, onSaved }) {
  const { showToast } = useToast();
  const [form, setForm] = useState(() => {
    const f = {};
    for (const field of blueprint.fields) f[field.key] = '';
    return f;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    for (const field of blueprint.fields) {
      if (field.required && !form[field.key]?.trim()) {
        setError(`${field.label} is required.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {};
      for (const field of blueprint.fields) {
        const value = (form[field.key] || '').trim();
        if (value) payload[field.key] = value;
      }
      const saveFn = SAVE_FN_BY_TYPE[integrationType];
      const res = await saveFn(orgId, payload);
      onSaved(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail : err.message || 'Save failed';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="section-header mb-0">Connect {blueprint.title}</h3>
            <p className="text-xs text-text-secondary mt-1">
              Credentials are encrypted at rest and never shown after save.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          {blueprint.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
                {field.label}
                {field.required && <span className="text-[#dc2626]"> *</span>}
              </label>
              <input
                className="input font-mono text-sm"
                type={field.type}
                autoComplete="new-password"
                placeholder={field.placeholder || ''}
                value={form[field.key]}
                onChange={update(field.key)}
                required={field.required}
              />
            </div>
          ))}
          {error && (
            <div className="text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn-outlined"
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving & testing…' : 'Save & Test'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── AI Configuration section ───────────────────────────────────────────────

const DEFAULT_TIERS = [
  { label: 'A', criteria: 'Immediate buyer, pre-approved, clear budget' },
  { label: 'B', criteria: 'Active buyer 30-90 days, pre-qualified' },
  { label: 'C', criteria: 'Exploring 90+ days, no pre-approval yet' },
  { label: 'D', criteria: 'Investor or unclear intent' },
];

const SYSTEM_PROMPT_PLACEHOLDER =
  'You are a professional real estate AI assistant for [Team Name]. Your role is to qualify buyers...';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
];

function AIConfigSection({ orgId, showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [defaultLanguage, setDefaultLanguage] = useState('en');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminGetAIConfig(orgId);
        if (cancelled) return;
        const cfg = res?.ai_config || {};
        setSystemPrompt(cfg.system_prompt || '');
        const framework = cfg.qualification_framework;
        const loadedTiers =
          framework && Array.isArray(framework.tiers) && framework.tiers.length > 0
            ? framework.tiers.map((t) => ({
                label: String(t.label || t.name || '').slice(0, 4),
                criteria: String(t.criteria || t.description || ''),
              }))
            : DEFAULT_TIERS;
        setTiers(loadedTiers);
        setDefaultLanguage(cfg.default_language || 'en');
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : err.message || 'Failed to load AI configuration.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const updateTier = (idx, key, value) => {
    setTiers((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t)),
    );
  };

  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTier = () => {
    setTiers((prev) => [...prev, { label: '', criteria: '' }]);
  };

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const cleanTiers = tiers
        .map((t) => ({
          label: String(t.label || '').trim(),
          criteria: String(t.criteria || '').trim(),
        }))
        .filter((t) => t.label || t.criteria);
      const payload = {
        system_prompt: systemPrompt,
        qualification_framework: { tiers: cleanTiers },
        default_language: defaultLanguage,
      };
      await adminSaveAIConfig(orgId, payload);
      showToast('AI configuration saved', { type: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : err.message || 'Save failed',
        { type: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card p-6">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-btn bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <Bot size={18} />
        </div>
        <div className="flex-1">
          <h2 className="text-[18px] font-bold text-text-primary mb-1">
            AI Configuration
          </h2>
          <p className="text-sm text-text-secondary">
            Customize the AI assistant personality and qualification framework
            for this client.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-5">
          <InlineSpinner label="Loading AI configuration…" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-5 text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="mt-6 space-y-6">
          {/* Part 1: System Prompt */}
          <div>
            <label className="block text-sm font-bold text-text-primary mb-2">
              System Prompt
            </label>
            <textarea
              className="input font-mono text-sm w-full"
              rows={16}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder={SYSTEM_PROMPT_PLACEHOLDER}
            />
          </div>

          {/* Part 2: Qualification Tiers */}
          <div>
            <label className="block text-sm font-bold text-text-primary mb-1">
              Buyer Qualification Tiers
            </label>
            <p className="text-xs text-text-secondary mb-3">
              Define how the AI categorizes buyers (e.g. A, B, C, D).
            </p>
            <div className="space-y-3">
              {tiers.map((tier, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <input
                    className="input text-center font-semibold"
                    style={{ width: 60 }}
                    value={tier.label}
                    onChange={(e) => updateTier(idx, 'label', e.target.value)}
                    placeholder="A"
                    maxLength={4}
                  />
                  <textarea
                    className="input flex-1 text-sm"
                    rows={4}
                    value={tier.criteria}
                    onChange={(e) => updateTier(idx, 'criteria', e.target.value)}
                    placeholder="Describe what this tier means…"
                  />
                  <button
                    type="button"
                    onClick={() => removeTier(idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#dc2626] border border-[#ef4444]/40 rounded-btn hover:bg-[#ef4444]/10"
                  >
                    <Trash2 size={12} />
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addTier}
              className="mt-3 btn-outlined inline-flex items-center gap-2 text-sm"
            >
              <Plus size={14} />
              Add Tier
            </button>
          </div>

          {/* Part 3: Default Language */}
          <div>
            <label className="block text-sm font-bold text-text-primary mb-2">
              Default Language
            </label>
            <select
              className="input"
              style={{ maxWidth: 240 }}
              value={defaultLanguage}
              onChange={(e) => setDefaultLanguage(e.target.value)}
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.value})
                </option>
              ))}
            </select>
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-2 border-t border-border-card">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Listings section ───────────────────────────────────────────────────────

const LISTING_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'sold', label: 'Sold' },
];

const EMPTY_LISTING_FORM = {
  title: '',
  address: '',
  city: '',
  price: '',
  bedrooms: '',
  bathrooms: '',
  sqft: '',
  description: '',
  status: 'active',
};

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function ListingsSection({ orgId, showToast }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminListListings(orgId);
      setListings(res.listings || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to load listings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const openAdd = () => {
    setEditingListing(null);
    setModalOpen(true);
  };

  const openEdit = (listing) => {
    setEditingListing(listing);
    setModalOpen(true);
  };

  const onDelete = async (listing) => {
    if (!window.confirm('Remove this listing? The AI will no longer reference it.')) return;
    try {
      await adminDeleteListing(orgId, listing.id);
      showToast('Listing removed', { type: 'success' });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : err.message || 'Delete failed', { type: 'error' });
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: 'bg-[#22c55e]/15 text-[#16a34a]',
      pending: 'bg-[#f59e0b]/15 text-[#d97706]',
      sold: 'bg-[#6b7280]/15 text-[#6b7280]',
    };
    return (
      <span className={`inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium capitalize ${map[status] || 'bg-[#6b7280]/15 text-[#6b7280]'}`}>
        {status || '—'}
      </span>
    );
  };

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5 border-b border-border-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-btn bg-accent/10 text-accent flex items-center justify-center shrink-0">
            <Home size={18} />
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-text-primary mb-0.5">
              Property Listings
            </h2>
            <p className="text-sm text-text-secondary">
              Active listings the AI can reference when talking to leads.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="btn-outlined inline-flex items-center gap-2 shrink-0"
        >
          <Plus size={14} />
          Add Listing
        </button>
      </div>

      {loading && (
        <div className="p-6">
          <InlineSpinner label="Loading listings…" />
        </div>
      )}

      {error && !loading && (
        <div className="m-4 text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
          {error}
        </div>
      )}

      {!loading && !error && listings.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-4 py-14 text-center">
          <p className="text-text-secondary">
            No listings yet. Add your first property.
          </p>
          <button
            type="button"
            onClick={openAdd}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14} />
            Add Listing
          </button>
        </div>
      )}

      {!loading && !error && listings.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border-card">
                <th className="px-5 py-3 font-semibold">Title</th>
                <th className="px-5 py-3 font-semibold">Address</th>
                <th className="px-5 py-3 font-semibold">Price</th>
                <th className="px-5 py-3 font-semibold">Beds / Baths / Sqft</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-border-card/60 hover:bg-bg-subtle transition-colors"
                >
                  <td className="px-5 py-3 font-medium text-text-primary max-w-[160px] truncate">
                    {l.title}
                  </td>
                  <td className="px-5 py-3 text-text-secondary max-w-[160px] truncate">
                    {l.address || l.city || '—'}
                  </td>
                  <td className="px-5 py-3 text-text-secondary tabular-nums">
                    {formatPrice(l.price)}
                  </td>
                  <td className="px-5 py-3 text-text-secondary tabular-nums">
                    {[
                      l.bedrooms != null ? `${l.bedrooms}bd` : null,
                      l.bathrooms != null ? `${l.bathrooms}ba` : null,
                      l.sqft != null ? `${Number(l.sqft).toLocaleString()} sqft` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td className="px-5 py-3">{statusBadge(l.status)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(l)}
                        className="text-text-secondary hover:text-text-primary transition-colors"
                        title="Edit listing"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(l)}
                        className="text-text-secondary hover:text-[#dc2626] transition-colors"
                        title="Delete listing"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <ListingModal
          orgId={orgId}
          listing={editingListing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
          showToast={showToast}
        />
      )}
    </section>
  );
}

function ListingModal({ orgId, listing, onClose, onSaved, showToast }) {
  const isEdit = listing != null;
  const [form, setForm] = useState(() => {
    if (!isEdit) return { ...EMPTY_LISTING_FORM };
    return {
      title: listing.title || '',
      address: listing.address || '',
      city: listing.city || '',
      price: listing.price != null ? String(listing.price) : '',
      bedrooms: listing.bedrooms != null ? String(listing.bedrooms) : '',
      bathrooms: listing.bathrooms != null ? String(listing.bathrooms) : '',
      sqft: listing.sqft != null ? String(listing.sqft) : '',
      description: listing.description || '',
      status: listing.status || 'active',
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        price: form.price !== '' ? Number(form.price) : null,
        bedrooms: form.bedrooms !== '' ? parseInt(form.bedrooms, 10) : null,
        bathrooms: form.bathrooms !== '' ? Number(form.bathrooms) : null,
        sqft: form.sqft !== '' ? parseInt(form.sqft, 10) : null,
        description: form.description.trim() || null,
        status: form.status,
      };
      if (isEdit) {
        await adminUpdateListing(orgId, listing.id, payload);
        showToast('Listing updated', { type: 'success' });
      } else {
        await adminCreateListing(orgId, payload);
        showToast('Listing added', { type: 'success' });
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : err.message || 'Save failed';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg card p-6 space-y-4 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h3 className="section-header mb-0">
            {isEdit ? 'Edit Listing' : 'Add Listing'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-text-secondary mb-1">
              Title <span className="text-[#dc2626]">*</span>
            </label>
            <input
              className="input"
              value={form.title}
              onChange={update('title')}
              placeholder="e.g. Oceanfront Penthouse"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Address</label>
              <input className="input" value={form.address} onChange={update('address')} placeholder="123 Ocean Dr" />
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">City</label>
              <input className="input" value={form.city} onChange={update('city')} placeholder="Miami Beach" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Price ($)</label>
              <input className="input tabular-nums" type="number" min="0" step="1000" value={form.price} onChange={update('price')} placeholder="1500000" />
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Beds</label>
              <input className="input tabular-nums" type="number" min="0" value={form.bedrooms} onChange={update('bedrooms')} placeholder="3" />
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Baths</label>
              <input className="input tabular-nums" type="number" min="0" step="0.5" value={form.bathrooms} onChange={update('bathrooms')} placeholder="2.5" />
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Sqft</label>
              <input className="input tabular-nums" type="number" min="0" value={form.sqft} onChange={update('sqft')} placeholder="2200" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">Description</label>
            <textarea className="input" rows={4} value={form.description} onChange={update('description')} placeholder="Key details about the property…" />
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">Status</label>
            <select className="input" value={form.status} onChange={update('status')}>
              {LISTING_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-outlined" disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Pipelines section ──────────────────────────────────────────────────────

const COLOR_OPTIONS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#22c55e', label: 'Green' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#ef4444', label: 'Red' },
  { value: '#EA580C', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#6b7280', label: 'Gray' },
];

const DEFAULT_STAGES = [
  { name: 'New Lead', color: '#3b82f6' },
  { name: 'Contacted', color: '#f59e0b' },
  { name: 'Qualified', color: '#22c55e' },
  { name: 'Proposal', color: '#a855f7' },
  { name: 'Closed Won', color: '#22c55e' },
  { name: 'Closed Lost', color: '#6b7280' },
];

function PipelinesSection({ orgId, showToast }) {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminListPipelines(orgId);
      setPipelines(res.pipelines || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to load pipelines.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const openAdd = () => {
    setEditingPipeline(null);
    setModalOpen(true);
  };

  const openEdit = (pipeline) => {
    setEditingPipeline(pipeline);
    setModalOpen(true);
  };

  const onDelete = async (pipeline) => {
    if (!window.confirm(`Delete pipeline "${pipeline.name}"? This cannot be undone.`)) return;
    try {
      await adminDeletePipeline(orgId, pipeline.id);
      showToast('Pipeline deleted', { type: 'success' });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : err.message || 'Delete failed', { type: 'error' });
    }
  };

  const onSetDefault = async (pipeline) => {
    try {
      await adminUpdatePipeline(orgId, pipeline.id, { is_default: true });
      showToast(`"${pipeline.name}" set as default`, { type: 'success' });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : err.message || 'Update failed', { type: 'error' });
    }
  };

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5 border-b border-border-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-btn bg-accent/10 text-accent flex items-center justify-center shrink-0">
            <Layers size={18} />
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-text-primary mb-0.5">
              Pipelines
            </h2>
            <p className="text-sm text-text-secondary">
              Custom deal stages for tracking leads through your process.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="btn-outlined inline-flex items-center gap-2 shrink-0"
        >
          <Plus size={14} />
          Add Pipeline
        </button>
      </div>

      {loading && (
        <div className="p-6">
          <InlineSpinner label="Loading pipelines…" />
        </div>
      )}

      {error && !loading && (
        <div className="m-4 text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
          {error}
        </div>
      )}

      {!loading && !error && pipelines.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-4 py-14 text-center">
          <p className="text-text-secondary">
            No pipelines yet. Add your first pipeline.
          </p>
          <button
            type="button"
            onClick={openAdd}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14} />
            Add Pipeline
          </button>
        </div>
      )}

      {!loading && !error && pipelines.length > 0 && (
        <div className="p-4 space-y-3">
          {pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              className="rounded-lg bg-[#0d1424] border border-border-card p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold text-text-primary">
                    {pipeline.name}
                  </span>
                  {pipeline.is_default && (
                    <span className="inline-flex items-center gap-1 rounded-badge px-2 py-0.5 text-xs font-medium bg-accent/15 text-accent">
                      <Star size={10} />
                      Default
                    </span>
                  )}
                </div>
                <div className="inline-flex items-center gap-2 shrink-0">
                  {!pipeline.is_default && (
                    <button
                      type="button"
                      onClick={() => onSetDefault(pipeline)}
                      className="btn-outlined text-xs px-2 py-1"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openEdit(pipeline)}
                    className="text-text-secondary hover:text-text-primary transition-colors"
                    title="Edit pipeline"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(pipeline)}
                    className="text-text-secondary hover:text-[#dc2626] transition-colors"
                    title="Delete pipeline"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {Array.isArray(pipeline.stages) && pipeline.stages.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pipeline.stages.map((stage, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-badge px-2.5 py-1 text-xs font-medium text-white"
                      style={{ backgroundColor: stage.color || '#6b7280' }}
                    >
                      {stage.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <PipelineModal
          orgId={orgId}
          pipeline={editingPipeline}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
          showToast={showToast}
        />
      )}
    </section>
  );
}

function PipelineModal({ orgId, pipeline, onClose, onSaved, showToast }) {
  const isEdit = pipeline != null;
  const [name, setName] = useState(isEdit ? pipeline.name : '');
  const [isDefault, setIsDefault] = useState(isEdit ? Boolean(pipeline.is_default) : false);
  const [stages, setStages] = useState(() => {
    if (isEdit && Array.isArray(pipeline.stages) && pipeline.stages.length > 0) {
      return pipeline.stages.map((s) => ({ name: s.name || '', color: s.color || '#6b7280' }));
    }
    return DEFAULT_STAGES.map((s) => ({ ...s }));
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateStage = (idx, key, value) =>
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));

  const removeStage = (idx) =>
    setStages((prev) => prev.filter((_, i) => i !== idx));

  const addStage = () =>
    setStages((prev) => [...prev, { name: '', color: '#6b7280' }]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!name.trim()) {
      setError('Pipeline name is required.');
      return;
    }
    const cleanStages = stages
      .map((s) => ({ name: String(s.name || '').trim(), color: s.color || '#6b7280' }))
      .filter((s) => s.name);
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), stages: cleanStages, is_default: isDefault };
      if (isEdit) {
        await adminUpdatePipeline(orgId, pipeline.id, payload);
        showToast('Pipeline updated', { type: 'success' });
      } else {
        await adminCreatePipeline(orgId, payload);
        showToast('Pipeline created', { type: 'success' });
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : err.message || 'Save failed';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg card p-6 space-y-4 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h3 className="section-header mb-0">
            {isEdit ? 'Edit Pipeline' : 'Add Pipeline'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-text-secondary mb-1">
              Pipeline Name <span className="text-[#dc2626]">*</span>
            </label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Buyer Pipeline"
              required
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-accent w-4 h-4"
            />
            <span className="text-sm text-text-primary">Set as default pipeline</span>
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs text-text-secondary">
                Stages
              </label>
            </div>
            <div className="space-y-2">
              {stages.map((stage, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <GripVertical size={14} className="text-text-secondary shrink-0" />
                  <input
                    className="input flex-1 text-sm"
                    value={stage.name}
                    onChange={(e) => updateStage(idx, 'name', e.target.value)}
                    placeholder="Stage name"
                  />
                  <select
                    className="input text-sm"
                    style={{ width: 110 }}
                    value={stage.color}
                    onChange={(e) => updateStage(idx, 'color', e.target.value)}
                  >
                    {COLOR_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span
                    className="w-5 h-5 rounded-full shrink-0 border border-white/10"
                    style={{ backgroundColor: stage.color }}
                  />
                  <button
                    type="button"
                    onClick={() => removeStage(idx)}
                    className="text-text-secondary hover:text-[#dc2626] transition-colors shrink-0"
                    title="Remove stage"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addStage}
              className="mt-3 btn-outlined inline-flex items-center gap-2 text-sm"
            >
              <Plus size={14} />
              Add Stage
            </button>
          </div>

          {error && (
            <div className="text-sm text-[#dc2626] bg-[#ef4444]/10 border border-[#ef4444]/40 rounded-btn px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-outlined" disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Webchat Widget section ─────────────────────────────────────────────────

function WebchatWidgetSection({ orgId, showToast }) {
  const [copied, setCopied] = useState(false);
  const widgetOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://atlacis.app';
  const snippet = `<script src="${widgetOrigin}/widget.js"></script>\n<script>AtlacisChat.init({ org_id: '${orgId}' })</script>`;

  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(snippet);
      } else {
        const ta = document.createElement('textarea');
        ta.value = snippet;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      showToast('Embed code copied to clipboard', { type: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showToast(err.message || 'Copy failed', { type: 'error' });
    }
  };

  return (
    <section className="card p-6">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-btn bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <MessageSquare size={18} />
        </div>
        <div className="flex-1">
          <h2 className="section-header mb-1">Webchat Widget</h2>
          <p className="text-sm text-text-secondary">
            Embeddable chat bubble. Paste the snippet below before the closing{' '}
            <code className="font-mono text-xs text-text-primary">&lt;/body&gt;</code>{' '}
            tag on the client&apos;s website.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-text-secondary">
            Embed code
          </div>
          <button
            type="button"
            onClick={onCopy}
            className="btn-outlined inline-flex items-center gap-2 text-xs px-3 py-1.5"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre className="bg-[#0a0f1a] border border-card-border rounded-btn p-3 text-xs text-text-primary font-mono overflow-x-auto whitespace-pre">
{snippet}
        </pre>
        <p className="mt-3 text-xs text-text-secondary">
          The widget posts to{' '}
          <code className="font-mono text-text-primary">/api/re/conversations/webchat</code>{' '}
          with this org&apos;s <code className="font-mono text-text-primary">org_id</code>.
          Each visitor gets a persistent session stored in their browser
          localStorage, so conversations continue across page reloads.
        </p>
      </div>
    </section>
  );
}
